import { useCallback, useEffect, useRef, useState } from 'react';

import CatalogList from './components/catalog-list';
import CategoryButtons from './components/category-buttons';

import { DEFAULT_LISTENER_DEBOUNCE_INTERVAL } from 'src/constants';
import { debounce } from 'src/utils';

import './catalog.scss';

type CategoriesRefs = Record<string, HTMLDivElement>;

interface IStickyState {
  isSticky: boolean;
  offset: number;
}

interface ISizesState {
  windowWidth: number;
  pageHeadingHeight: number;
}

export function getCatalogButtonsTop(sizes: ISizesState) {
  const { pageHeadingHeight, windowWidth } = sizes;
  const mainLayoutGap = windowWidth < 768 ? 24 : 40; // gap of .main-layout
  const mainLayoutContentGap = windowWidth < 768 ? 28 : windowWidth < 1280 ? 48 : 46; // gap of .main-layout__content
  return mainLayoutGap + mainLayoutContentGap + pageHeadingHeight - 16; // - vertical space
}

const Catalog = () => {
  const [sticky, setSticky] = useState<IStickyState>({ isSticky: false, offset: 0 });
  const catalogRef = useRef<HTMLDivElement | null>(null);
  const buttonsRef = useRef<HTMLDivElement | null>(null);
  const categoriesRefs = useRef<CategoriesRefs>({});
  const [sizes, setSizes] = useState<ISizesState>({ windowWidth: 0, pageHeadingHeight: 0 });

  useEffect(() => {
    const onWindowResize = () => {
      const pageHeadingHeight =
        (document.querySelector('.page-heading') as HTMLElement)?.offsetHeight ?? 0;

      setSizes({
        pageHeadingHeight,
        windowWidth: window.innerWidth,
      });
    };

    onWindowResize();

    const handleResize = debounce(onWindowResize, DEFAULT_LISTENER_DEBOUNCE_INTERVAL);

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCatalogScroll = useCallback(() => {
    if (buttonsRef.current) {
      const buttonsBoundings = buttonsRef.current.getBoundingClientRect();
      const catalogButtonsTop = getCatalogButtonsTop(sizes);

      if (window.scrollY >= catalogButtonsTop) {
        setSticky({ isSticky: true, offset: buttonsBoundings.height });
      } else {
        setSticky({ isSticky: false, offset: 0 });
      }
    }
  }, [sizes]);

  useEffect(() => {
    window.addEventListener('scroll', handleCatalogScroll);

    return () => {
      window.removeEventListener('scroll', handleCatalogScroll);
    };
  }, [handleCatalogScroll]);

  const setCategoryRef = useCallback((category: string, element: HTMLDivElement) => {
    categoriesRefs.current[category] = element;
  }, []);

  const scrollToCategory = (category: string) => {
    const categoryRef = categoriesRefs.current[category];
    if (categoryRef) {
      const buttons = buttonsRef.current;
      const buttonsHeight = buttons?.scrollHeight ?? 0;
      const parentTop = catalogRef?.current?.parentElement?.getBoundingClientRect().top ?? 0;
      const buttonsTop = buttons?.getBoundingClientRect().top ?? 0;
      const offsetTop = parentTop > 0 ? parentTop - (buttons?.clientHeight ?? 0) * 0.6 : buttonsTop;
      window.scrollTo({
        top: categoryRef.offsetTop - buttonsHeight - offsetTop,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="catalog" ref={catalogRef}>
      <div className="catalog__buttons" style={{ paddingTop: sticky.offset }}>
        <CategoryButtons
          ref={buttonsRef}
          scrollToCategory={scrollToCategory}
          isSticky={sticky.isSticky}
        />
      </div>
      <CatalogList setCategoryRef={setCategoryRef} />
    </div>
  );
};

export default Catalog;
